import React, { useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { Button } from '../Button';
import type { ClxChannel, ClxPeriod, ClxStatus, CollectionsData, PaymentLinkRow } from '../../collections/types';
import { CreateLinkDrawer } from './CreateLinkDrawer';

export interface CollectionsViewProps {
  data: CollectionsData;
  onOpenSettings?: () => void;
  onOpenPatient?: (patientId: string) => void;
  onFixAccount?: () => void;
}

export function fmtMoney(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const CHANNEL_ICON: Record<ClxChannel, IconKind> = { text: 'chat', email: 'mail', qr: 'grid' };
const CHANNEL_WORD: Record<ClxChannel, string> = { text: 'Text', email: 'Email', qr: 'QR at the desk' };

const STATUS_PILL: Record<ClxStatus, { label: string; fg: string; bg: string }> = {
  paid: { label: 'Paid', fg: 'var(--money-green)', bg: 'var(--money-green-tint)' },
  waiting: { label: 'Waiting', fg: 'var(--brand-indigo)', bg: 'var(--brand-indigo-tint)' },
  expired: { label: 'Expired', fg: 'var(--warn-amber)', bg: 'var(--warn-amber-fill)' },
  canceled: { label: 'Cancelled', fg: 'var(--text-muted)', bg: 'var(--chip-fill)' },
};

const PERIODS: { id: ClxPeriod; label: string }[] = [
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: 'year', label: 'This year' },
];

const STATUS_ORDER: ClxStatus[] = ['waiting', 'paid', 'expired', 'canceled'];

const COLS = 'grid-cols-[minmax(0,1.5fr)_104px_minmax(0,1.35fr)_128px_104px_minmax(0,1.8fr)_34px]';

export function CollectionsView({ data, onOpenSettings, onOpenPatient, onFixAccount }: CollectionsViewProps) {
  const [period, setPeriod] = useState<ClxPeriod>(data.period);
  const [filter, setFilter] = useState<'all' | ClxStatus>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [prefillRow, setPrefillRow] = useState<PaymentLinkRow | null>(null);

  const connected = data.accountConnected;

  // Waiting sorts first (§4.3); the rest keep their newest-first order.
  const ordered = useMemo(() => {
    return [...data.rows].sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
  }, [data.rows]);

  const count = (s: 'all' | ClxStatus) => (s === 'all' ? ordered.length : ordered.filter((r) => r.status === s).length);
  const rows = filter === 'all' ? ordered : ordered.filter((r) => r.status === filter);

  const chips: { id: 'all' | ClxStatus; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'waiting', label: 'Waiting' },
    { id: 'paid', label: 'Paid' },
    { id: 'expired', label: 'Expired' },
    { id: 'canceled', label: 'Cancelled' },
  ];

  const openCreate = () => {
    setPrefillRow(null);
    setDrawerOpen(true);
  };
  const openSendAgain = (r: PaymentLinkRow) => {
    setPrefillRow(r);
    setDrawerOpen(true);
  };

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1200px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      {/* header — declares its own actions (§2, N24) */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="pc-eyebrow flex items-center gap-1.5 text-[var(--text-secondary)]">
            <Icon kind="credit" size={12} stroke={2} style={{ color: 'var(--brand-indigo)' }} />
            <span>
              Collections · {data.waitingCount} links waiting · {fmtMoney(data.notYetPaid)} not yet paid ·{' '}
              {fmtMoney(data.collected30d)} collected through links in 30 days
            </span>
          </div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
            {data.titleLead} <span className="text-[var(--brand-indigo)]">{data.titleAccent}</span>
          </h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-secondary)]">{data.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onOpenSettings}>
            Settings
          </Button>
          <Button variant="primary" size="sm" iconLeft="plus" disabled={!connected} onClick={openCreate}>
            Create link
          </Button>
        </div>
      </div>

      {/* disconnected-account AlertBar (§2.1) — absent when connected */}
      {!connected ? (
        <section
          aria-label="Payment account disconnected"
          className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--alert-red)_35%,transparent)] bg-[var(--alert-red-tint)] px-4 py-2.5"
        >
          <Icon kind="alert" size={15} stroke={2} style={{ color: 'var(--alert-red)' }} />
          <span className="text-[13px] font-semibold text-[var(--alert-red)]">
            Payment links are switched off — the payment account needs reconnecting.
          </span>
          <span className="text-[12px] text-[var(--text-secondary)]">Nothing can be sent or paid until it is.</span>
          <button
            type="button"
            className="ml-auto text-[12.5px] font-semibold text-[var(--alert-red)] hover:underline"
            onClick={onFixAccount}
          >
            Fix it in Settings →
          </button>
        </section>
      ) : null}

      {/* filter row */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {chips.map((c) => (
          <Chip key={c.id} label={c.label} count={count(c.id)} on={filter === c.id} onClick={() => { setFilter(c.id); }} />
        ))}
        <div className="ml-auto">
          <label className="sr-only" htmlFor="clx-period">
            Period
          </label>
          <select
            id="clx-period"
            value={period}
            onChange={(e) => { setPeriod(e.target.value as ClxPeriod); }}
            className="rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
          >
            {PERIODS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* table */}
      <div className="mt-3 overflow-hidden rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]">
        <div className={cn('grid items-center gap-x-3 border-b border-[var(--border-default)] bg-[var(--surface-header)] px-3 py-2.5 font-[var(--font-mono)] text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]', COLS)}>
          <span>Patient</span>
          <span className="text-right">Amount</span>
          <span>Sent to</span>
          <span className="inline-flex items-center gap-1">
            Sent <Icon kind="down" size={10} stroke={2.5} style={{ color: 'var(--text-muted)' }} />
          </span>
          <span>Status</span>
          <span>What happened</span>
          <span />
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-[12.5px] text-[var(--text-muted)]">
            No {filter === 'all' ? '' : STATUS_PILL[filter].label.toLowerCase() + ' '}links in the {PERIODS.find((p) => p.id === period)?.label.toLowerCase()}.
          </div>
        ) : (
          rows.map((r) => (
            <Row
              key={r.id}
              r={r}
              connected={connected}
              onOpenPatient={onOpenPatient}
              onSendAgain={() => { openSendAgain(r); }}
            />
          ))
        )}
      </div>

      {/* footer (§7) */}
      <div className="mt-3 flex flex-wrap items-start justify-between gap-x-6 gap-y-1 text-[11.5px] text-[var(--text-muted)]">
        <span>
          {ordered.length} links in the {PERIODS.find((p) => p.id === period)?.label.toLowerCase()}. Every one of them is
          also in the <span className="font-semibold text-[var(--text-secondary)]">Communication log</span>.
        </span>
        <span className="text-right">
          Chasing an overdue balance on a schedule is the{' '}
          <span className="font-semibold text-[var(--text-secondary)]">Accounts receivable</span> agent&rsquo;s job, not
          this page&rsquo;s.
        </span>
      </div>

      <CreateLinkDrawer
        open={drawerOpen}
        patient={data.drawerPatient}
        prefill={prefillRow}
        onClose={() => { setDrawerOpen(false); }}
      />
    </div>
  );
}

function Row({
  r,
  connected,
  onOpenPatient,
  onSendAgain,
}: {
  r: PaymentLinkRow;
  connected: boolean;
  onOpenPatient: ((id: string) => void) | undefined;
  onSendAgain: () => void;
}) {
  const pill = STATUS_PILL[r.status];
  const cantPay = r.status === 'waiting' && !connected;
  return (
    <div className={cn('grid items-center gap-x-3 border-t border-[var(--border-default)] px-3 py-3.5 text-[13px] leading-[1.5]', COLS)}>
      {/* patient = identity (§4.1) */}
      <div className="min-w-0">
        <div className="truncate font-semibold text-[var(--text-primary)]">{r.patient}</div>
        <div className="truncate text-[11.5px] text-[var(--text-muted)]">{r.label ?? '—'}</div>
      </div>
      {/* amount */}
      <div className="text-right font-[var(--font-mono)] text-[13px] font-bold text-[var(--text-primary)]">
        {fmtMoney(r.amount)}
      </div>
      {/* sent to */}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <Icon kind={CHANNEL_ICON[r.channel]} size={13} stroke={1.9} style={{ color: 'var(--icon-default)' }} />
          <span className="truncate text-[var(--text-secondary)]">{r.destination}</span>
        </div>
        <div className="truncate text-[11.5px] text-[var(--text-muted)]">{CHANNEL_WORD[r.channel]}</div>
      </div>
      {/* sent */}
      <div className="min-w-0">
        <div className="truncate text-[var(--text-secondary)]">{r.sentRelative}</div>
        {r.sentRelative !== r.sentAbsolute ? (
          <div className="truncate font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">{r.sentAbsolute}</div>
        ) : null}
      </div>
      {/* status */}
      <div>
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
          style={{ color: pill.fg, background: pill.bg }}
        >
          {pill.label}
        </span>
      </div>
      {/* what happened (§4.2) */}
      <div className="min-w-0">
        <div className="truncate">
          <span className={cn('font-semibold', r.status === 'paid' ? 'text-[var(--money-green)]' : 'text-[var(--text-primary)]')}>
            {r.outcomeLead}
          </span>
          {r.outcomeExpiry ? <span className="text-[var(--text-muted)]"> · {r.outcomeExpiry}</span> : null}
        </div>
        <div className="truncate text-[11.5px] text-[var(--text-muted)]">
          {cantPay ? 'This link cannot be paid right now. ' : ''}
          {r.outcomeSub}
        </div>
      </div>
      {/* menu */}
      <RowMenu r={r} onOpenPatient={onOpenPatient} onSendAgain={onSendAgain} />
    </div>
  );
}

function RowMenu({
  r,
  onOpenPatient,
  onSendAgain,
}: {
  r: PaymentLinkRow;
  onOpenPatient: ((id: string) => void) | undefined;
  onSendAgain: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [reason, setReason] = useState('');
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const close = () => {
    setOpen(false);
    setCanceling(false);
    setReason('');
  };

  const copyLink = () => {
    if (r.payUrl) {
      void navigator.clipboard.writeText(r.payUrl);
    }
    setCopied(true);
    window.setTimeout(() => { setCopied(false); }, 1400);
  };

  return (
    <div ref={ref} className="relative flex justify-end">
      <button
        type="button"
        aria-label="Row actions"
        onClick={() => { setOpen((v) => !v); }}
        className="flex h-7 w-7 items-center justify-center rounded-[var(--r-md)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
      >
        <Icon kind="dots" size={16} stroke={2} />
      </button>
      {open ? (
        <>
          <button type="button" aria-hidden className="fixed inset-0 z-40 cursor-default" onClick={close} tabIndex={-1} />
          <div className="absolute right-0 top-8 z-50 w-[248px] overflow-hidden rounded-[var(--r-lg)] border border-[var(--border-default)] bg-[var(--surface-card)] py-1 shadow-[var(--shadow-modal)]">
            {canceling ? (
              <div className="px-3 py-2">
                <div className="mb-1.5 text-[12px] font-semibold text-[var(--text-primary)]">Cancel this link?</div>
                <input
                  autoFocus
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); }}
                  placeholder="One-line reason (shows on the row)"
                  className="w-full rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-sunken)] px-2.5 py-1.5 text-[12px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
                />
                <div className="mt-2 flex justify-end gap-2">
                  <button type="button" onClick={close} className="rounded-[var(--r-md)] px-2.5 py-1 text-[12px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]">
                    Keep it
                  </button>
                  <button
                    type="button"
                    disabled={reason.trim() === ''}
                    onClick={close}
                    className="rounded-[var(--r-md)] bg-[var(--alert-red)] px-2.5 py-1 text-[12px] font-semibold text-[var(--text-on-accent)] disabled:opacity-40"
                  >
                    Cancel link
                  </button>
                </div>
              </div>
            ) : (
              <>
                {r.status === 'waiting' ? (
                  <MenuItem icon="copy" label={copied ? 'Copied' : 'Copy link'} onClick={copyLink} />
                ) : null}
                {r.status === 'expired' || r.status === 'canceled' ? (
                  <MenuItem icon="refresh" label="Send again" onClick={() => { setOpen(false); onSendAgain(); }} />
                ) : null}
                {r.status === 'waiting' ? (
                  <MenuItem icon="x" label="Cancel link" onClick={() => { setCanceling(true); }} />
                ) : null}
                <MenuItem
                  icon="arrow-r"
                  label="Open patient"
                  onClick={() => {
                    close();
                    onOpenPatient?.(r.patientId);
                  }}
                />
              </>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function MenuItem({ icon, label, onClick }: { icon: IconKind; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
    >
      <Icon kind={icon} size={14} stroke={2} style={{ color: 'var(--text-muted)' }} />
      {label}
    </button>
  );
}

function Chip({ label, count, on, onClick }: { label: string; count: number; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold',
        on
          ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo)] text-[var(--text-on-accent)]'
          : 'border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
      )}
    >
      {label}
      <span className={cn('font-[var(--font-mono)] text-[11px] font-bold', on ? 'text-[var(--text-on-accent)]' : 'text-[var(--text-muted)]')}>
        {count}
      </span>
    </button>
  );
}
